import { inject, Injectable } from '@angular/core';
import { Subscription, forkJoin, Subject } from 'rxjs';

import { LeaderboardHttpService } from '../http/http.service';
import { UserSimpleGamification } from '../../model/user.model';
import { NonNullableFormBuilder } from '@angular/forms';

import { BroadcastService, BroadcastMessage } from '@app/core/service';

@Injectable()
export class LeaderboardPreviewGamificationService {
    private readonly leaderboardHttpService = inject(LeaderboardHttpService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly fb = inject(NonNullableFormBuilder);

    /**
     * Loading Flag
     */
    loading = false;

    /**
     * User
     * Record<string, UserSimpleGamification>
     */
    _user: any = {};

    /**
     * Users
     *  Record<string, UserSimpleGamification[]>
     */
    _users: any = {};

    readonly form: any = this.fb.group({
        range: this.fb.control<LeaderboardPreviewGamificationService['ranges'][number]>('week'),
    });

    /**
     * Range FormControl
     */
    readonly range = this.form.controls.range;

    /**
     * Ranges
     */
    readonly ranges = <const>['week', 'month', 'yeartodate', 'alltime'];

    /**
     * viewed User
     * UserSimpleGamification
     */
    viewedUser: any = null;

    /**
     * viewed Users
     * UserSimpleGamification[]
     */
    viewedUsers: any = null;

    /**
     * Subject
     */
    private updatesEvent = new Subject();

    /**
     * Subscriptions
     */
    private subscriptions = new Subscription();

    init(): void {
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Subject
     */
    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Preview
     */
    leaderboard(count: number) {
        // create empty tree
        this.createEmptyTree();
        // check if page already loaded
        if (this._users[this.range.value]) {
            this.viewedUser = this._user[this.range.value];
            this.viewedUsers = this._users[this.range.value];
        } else {
            this.loadLeaderboard();
        }
        return this.sliceLeaderboard(count);
    }

    /**
     * Load Leaderboard
     */
    loadLeaderboard(): void {
        if (this.loading) {
            return;
        }

        const range = this.range.value;

        this.loading = true;
        this.updatesEvent.next(true);

        this.subscriptions.add(
            forkJoin([this.leaderboardHttpService.getUserGamification(range), this.leaderboardHttpService.getLeaderboardGamification(range, 10, 0)]).subscribe(
                (data) => this.leaderboardResults(range, data[0], data[1].users),
            ),
        );
    }

    /**
     * Trial - reset leaderboard
     */
    trialResetLeaderboard() {
        this.reset();
    }

    /**
     * Leaderboard Results Handler
     */
    private leaderboardResults(range: string, user: UserSimpleGamification, users: UserSimpleGamification[]) {
        this._users[range] = users;
        this._user[range] = user;
        this.loading = false;
        this.updatesEvent.next(true);
    }

    /**
     * Slice leaderboard
     */
    private sliceLeaderboard(count: number) {
        if (!this.viewedUsers) {
            return null;
        }

        const users = this.viewedUsers.slice(0, count);
        const user = this.viewedUser;

        if (users.findIndex((userList: any) => userList.id === user.id) === -1) {
            users[users.length - 1] = user;
        }

        return users;
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        // create object key
        if (!this._users[this.range.value]) {
            this._users[this.range.value] = null;
            this._user[this.range.value] = null;
        }
    }

    /**
     * Reset Pagination and Form
     */
    private reset(): void {
        this._user = {};
        this._users = {};
        this.viewedUser = null;
        this.viewedUsers = null;
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'discussion-topic-created':
            case 'discussion-topic-reply-created':
            case 'discussion-reply-reply-created':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
